import { styled } from "@mui/material/styles";
import { DataGrid, DataGridCardProps, FlexRowCenter, FormControlLabel, GalleryPlaceholder, Grid, InfoIconButton, Radio, RadioGroup, Text } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import RulePane from "csi/middleware/attributeRule/RulePane";
import { RuleStoreContext } from "csi/middleware/attributeRule/RuleStore";
import { C8Node } from "csi/typings";
import { observer } from "mobx-react-lite";
import React, { ChangeEvent, Fragment, FunctionComponent } from "react";
import PPAttributeSetting from "../PPAttributeSetting";
import PPProductCard, { PPProductCardSkeleton } from "./PPProductCard";
import { usePPWizardStore } from "./PPWizardStore";
import { ProductGalleryStoreContext } from "./ProductGalleryStore";

const PREFIX = "PPWizardDefinePPRuleAndPreview";

const classes = {
	info: `${PREFIX}info`,
	transparentCards: `${PREFIX}transparentCards`,
};

const StyledGrid = styled(Grid)((
	{
		theme,
	},
) => ({
	[`& .${classes.info}`]: {
		marginLeft: 4,
	},

	[`& .${classes.transparentCards}`]: {
		opacity: 0.25,
	},
}));

const AddFromRulesLabel = observer(() => {
	const { primaryType, resultCountLimit, resultCountOverLimit } = usePPWizardStore();
	const { info } = classes;

	const resParams = [useLocale().type(primaryType, true), resultCountLimit];

	return (
		<FlexRowCenter height={20}>
			<Text res="PPAddFromRules" resParams={resParams} />
			{resultCountOverLimit && (
				<InfoIconButton
					className={info}
					res="PPAddFromRulesLimited"
					resParams={resParams}
				/>
			)}
		</FlexRowCenter>
	);
});

const RadioButtons = observer(() => {
	const { disableAddStyles, primaryType, shouldAddStyles, setAddStyles } = usePPWizardStore();
	const resParams = [useLocale().type(primaryType, true)];

	const onRadio = (event: ChangeEvent<HTMLInputElement>) => {
		const { value } = event.target as HTMLInputElement;
		setAddStyles(value === "true");
	};

	return (
		<RadioGroup
			onChange={onRadio}
			value={String(shouldAddStyles)}
		>
			<FormControlLabel
				control={<Radio />}
				disabled={disableAddStyles}
				label={<AddFromRulesLabel />}
				labelPlacement="end"
				value="true"
			/>
			<FormControlLabel
				control={<Radio />}
				labelPlacement="end"
				res="PPDontAddFromRules"
				resParams={resParams}
				value="false"
			/>
		</RadioGroup>
	);
});

const PPGalleryPlaceholder = () => {
	return (
		<GalleryPlaceholder titleProps={{ res: "PPNoMatchesForRules" }} />
	);
};

const PPGalleryMessage = observer(() => {
	const wizardStore = usePPWizardStore();
	const { primaryType, productsAreLoading, resultCount } = wizardStore;
	const typeName = useLocale().type(primaryType, resultCount != 1);

	return productsAreLoading ?
		<Text res="FindingMatches" variant="subtitle1" />
		: (
			<Text
				res="PPProductsMatchRules"
				resParams={[resultCount, typeName]}
				variant="subtitle1"
			/>
		);
});

const PPProductCardForEditRule: FunctionComponent<DataGridCardProps<C8Node>> = (props) => <PPProductCard hideUsedInSection {...props} />;

const PPGallery = observer(() => {
	const wizardStore = usePPWizardStore();
	const { productStore, resultCount, shouldAddStyles } = wizardStore;
	const { transparentCards } = classes;

	return (
		<DataGrid
			CardComponent={PPProductCardForEditRule}
			PlaceholderComponent={PPGalleryPlaceholder}
			SkeletonComponent={PPProductCardSkeleton}
			className={shouldAddStyles || !resultCount ? null : transparentCards}
			columns={4}
			spacing={3}
			store={productStore}
		/>
	);
});

export const PPWizardDefinePPRuleAndPreview = observer(() => {
	const wizardStore = usePPWizardStore();
	const { galleryStore, ruleStore, attributeSettingStore } = wizardStore;

	return (
		<StyledGrid
			container
			direction="column"
			spacing={5}
		>
			<Grid item>
				<RuleStoreContext.Provider value={ruleStore}>
					<RulePane
						SettingComponent={PPAttributeSetting}
						settingComponentStore={attributeSettingStore}
					/>
				</RuleStoreContext.Provider>
			</Grid>
			{galleryStore && (
				<Fragment>
					<Grid item>
						<RadioButtons />
					</Grid>
					<Grid item>
						<Grid
							container
							direction="column"
							spacing={2}
						>
							<Grid item>
								<PPGalleryMessage />
							</Grid>
							<Grid item>
								<ProductGalleryStoreContext.Provider value={galleryStore}>
									<PPGallery />
								</ProductGalleryStoreContext.Provider>
							</Grid>
						</Grid>
					</Grid>
				</Fragment>
			)}
		</StyledGrid>
	);
});

export default PPWizardDefinePPRuleAndPreview;